/**
 * The root component: reads the search and renders the three layers inside
 * the chrome (`features/shell`, T48) and behind the session gate
 * (`features/auth`, T47).
 *
 * - The base screen, the overlay and the sheet are each lazy-loaded from
 *   their feature's `index.tsx`, so the first paint carries none of them; once
 *   it has drawn, `WarmLayers` loads them all in idle time (`D190`), so the
 *   first open of each is not a chunk load at the tap.
 * - `overlay=diagnostics` exists only in DEV builds: the import sits behind
 *   `import.meta.env.DEV`, which Vite replaces with `false` in a production
 *   build, so the diagnostics chunk is tree-shaken away.
 * - The base screen goes to `AppLayout`'s `screen` PROP and only the two
 *   layers are `children`. It used to hand all three over as children, with
 *   the first taken as the screen: anything inserted above it — a provider, a
 *   bridge — became "the screen", took the fade key, and pushed the real
 *   screen into the layers, with no type error and no failing test.
 * - A layer is keyed by NOTHING but its own component. `muscle` and `card`
 *   are search updates to the same layer; keying it on its params would
 *   remount it per change and re-acquire a video player each time (T-107).
 * - When the coach and a sheet coexist, the one opened last renders last
 *   (`topLayer`), so it sits on top in DOM order too.
 * - A sheet pushed over a sheet STACKS (`D225`): the one underneath stays
 *   mounted and open, inert, until Back uncovers it as it was.
 * - The ONE `ToastProvider` (T14, T46) wraps everything, ABOVE the gate:
 *   sign-in and onboarding have a toast channel too, and the PWA update
 *   prompt (`UpdatePromptHost`) is raised from outside React at any moment,
 *   signed in or not. A second provider is a second viewport, and two of them
 *   show two toasts stacked in the same place.
 */
import { useLocation } from "@tanstack/react-router";
import {
  type ComponentType,
  type LazyExoticComponent,
  lazy,
  type ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { SessionGate } from "../features/auth";
import { TranscriptPrefetchBridge } from "../features/coach/transcript-prefetch";
import { PendingInviteDoor } from "../features/invite/pending-door";
import { PendingSharedPlanDoor } from "../features/plan-share/pending-door";
import { ClockBridge } from "../features/profile/use-clock-sync";
import { WeightUnitBridge } from "../features/profile/weight-unit-bridge";
import { PushRouting } from "../features/push";
import { AppLayout } from "../features/shell";
import { ToastProvider } from "../ui/overlay";
import { LayerPresenceProvider, type LayerSlot } from "../ui/overlay/presence";
import { useAndroidBackButton } from "./back-button";
import { overlayOf, topLayer, useAppSearch } from "./layers";
import { UpdatePromptHost } from "./pwa/update-host";
import {
  dropExited,
  layerKey,
  type Named,
  type RetainedLayer,
  retainLayers,
} from "./retained-layers";
import { ScreenStack } from "./screen-stack";
import {
  type Overlay,
  type Screen,
  type Search,
  type Sheet,
  screenOf,
} from "./search";

type Lazy = LazyExoticComponent<ComponentType>;

const SCREENS: Record<Screen, Lazy> = {
  today: lazy(() => import("../features/today")),
  workout: lazy(() => import("../features/workout")),
  plan: lazy(() => import("../features/plan")),
  progress: lazy(() => import("../features/progress")),
  profile: lazy(() =>
    import("../features/profile").then((m) => ({ default: m.ProfileScreen }))
  ),
};

const DiagnosticsOverlay: Lazy | null = import.meta.env.DEV
  ? lazy(() =>
      import("../diagnostics").then((m) => ({ default: m.DiagnosticsScreen }))
    )
  : null;

const OVERLAYS: Record<Exclude<Overlay, "diagnostics">, Lazy> = {
  coach: lazy(() => import("../features/coach")),
  search: lazy(() => import("../features/search")),
  profile: lazy(() => import("../features/profile")),
  wrapped: lazy(() => import("../features/wrapped")),
  body: lazy(() => import("../features/body")),
};

const SHEETS: Record<Sheet, Lazy> = {
  exercise: lazy(() => import("../features/exercise")),
  catalogue: lazy(() =>
    import("../features/exercise").then((m) => ({ default: m.CatalogueSheet }))
  ),
  draft: lazy(() => import("../features/draft")),
  exercises: lazy(() =>
    import("../features/workout").then((m) => ({ default: m.ExercisesSheet }))
  ),
  end: lazy(() =>
    import("../features/workout").then((m) => ({ default: m.EndSheet }))
  ),
  summary: lazy(() =>
    import("../features/workout").then((m) => ({ default: m.SummarySheet }))
  ),
  quarantine: lazy(() => import("../features/workout/quarantine")),
  set: lazy(() => import("../features/workout/correct")),
  swap: lazy(() => import("../features/swap/sheet")),
  logbook: lazy(() => import("../features/logbook")),
  builder: lazy(() => import("../features/plan-builder")),
  days: lazy(() => import("../features/plan/days-sheet")),
  quick: lazy(() => import("../features/quick")),
  report: lazy(() => import("../features/report")),
  news: lazy(() => import("../features/community/news-sheet")),
  roadmap: lazy(() => import("../features/community/roadmap-sheet")),
  suggest: lazy(() => import("../features/community/suggest-sheet")),
  planshare: lazy(() => import("../features/plan-share/share-sheet")),
  shared: lazy(() => import("../features/plan-share/shared-sheet")),
  invite: lazy(() => import("../features/invite/invite-sheet")),
  board: lazy(() => import("../features/community/board-sheet")),
  gallery: lazy(() => import("../features/plan-share/gallery-sheet")),
  coaching: lazy(() => import("../features/coaching/hub-sheet")),
  "coaching-group": lazy(() => import("../features/coaching/group-sheet")),
  "coaching-student": lazy(() => import("../features/coaching/student-sheet")),
  "coaching-chat": lazy(() => import("../features/coaching/chat-sheet")),
  clubs: lazy(() => import("../features/clubs/clubs-sheet")),
  club: lazy(() => import("../features/clubs/club-sheet")),
};

/**
 * Every module the maps above load, in the order `WarmLayers` loads them
 * (`D190`): the screens the tab bar reaches, the sheets a card opens, then
 * the overlays — the trainer last, being by far the largest chunk. A module
 * missing here is only loaded at its first tap, as before; this list cannot
 * make a layer load the wrong chunk, because `lazy` never reads it.
 */
const WARM_ORDER: readonly (() => Promise<unknown>)[] = [
  () => import("../features/today"),
  () => import("../features/workout"),
  () => import("../features/plan"),
  () => import("../features/progress"),
  () => import("../features/profile"),
  () => import("../features/exercise"),
  () => import("../features/swap/sheet"),
  () => import("../features/logbook"),
  () => import("../features/plan-builder"),
  () => import("../features/plan/days-sheet"),
  () => import("../features/quick"),
  () => import("../features/report"),
  () => import("../features/community/news-sheet"),
  () => import("../features/community/roadmap-sheet"),
  () => import("../features/community/suggest-sheet"),
  () => import("../features/plan-share/share-sheet"),
  () => import("../features/plan-share/shared-sheet"),
  () => import("../features/invite/invite-sheet"),
  () => import("../features/community/board-sheet"),
  () => import("../features/plan-share/gallery-sheet"),
  () => import("../features/coaching/hub-sheet"),
  () => import("../features/coaching/group-sheet"),
  () => import("../features/coaching/student-sheet"),
  () => import("../features/coaching/chat-sheet"),
  () => import("../features/clubs/clubs-sheet"),
  () => import("../features/clubs/club-sheet"),
  () => import("../features/draft"),
  () => import("../features/workout/correct"),
  () => import("../features/workout/quarantine"),
  () => import("../features/search"),
  () => import("../features/body"),
  () => import("../features/wrapped"),
  () => import("../features/coach"),
];

/**
 * Loads every layer's chunk once the app has drawn, one per idle moment
 * (`D190`). Without it the first open of each screen, sheet and overlay
 * loaded and parsed its chunk at the tap, behind `fallback={null}`: the tap
 * looked ignored until it had. Mounted inside the gate, so a signed-out
 * launch loads none of it. A chunk that fails is skipped silently — the
 * layer's own `lazy` loads it again when opened, and reports what fails then.
 */
function WarmLayers() {
  useEffect(() => {
    let cancelled = false;
    let handle = 0;
    const idle = (run: () => void) => {
      handle =
        typeof requestIdleCallback === "function"
          ? requestIdleCallback(run, { timeout: 2000 })
          : window.setTimeout(run, 50);
    };
    const warm = (index: number) => {
      const load = WARM_ORDER[index];
      if (cancelled || load === undefined) {
        return;
      }
      const next = () => idle(() => warm(index + 1));
      load().then(next, next);
    };
    idle(() => warm(0));
    return () => {
      cancelled = true;
      if (typeof cancelIdleCallback === "function") {
        cancelIdleCallback(handle);
      } else {
        window.clearTimeout(handle);
      }
    };
  }, []);
  return null;
}

/** One base screen, for the stack that keeps every visited one mounted. */
function renderScreen(screen: Screen): ReactNode {
  const ScreenView = SCREENS[screen];
  return (
    <Suspense fallback={null}>
      <ScreenView />
    </Suspense>
  );
}

function overlayComponent(overlay: Overlay): Lazy | null {
  return overlay === "diagnostics" ? DiagnosticsOverlay : OVERLAYS[overlay];
}

/**
 * The longest a closed layer stays mounted waiting for its exit to report
 * (`D193`). Every exit is 200-450 ms; a layer whose root never reports —
 * one that draws no Base UI root, or whose popup was already closed — is
 * removed here instead of staying for ever.
 */
const EXIT_SAFETY_MS = 900;

type Retained = RetainedLayer<Lazy, Search>;

/**
 * The layers of one slot the shell keeps mounted: the one the URL names, any
 * it covers, and any still playing its exit (`retained-layers.ts`). Derived
 * during render — React's pattern for state that follows a prop — so a layer
 * the URL drops is marked leaving in the same render that used to unmount it.
 */
function useRetained(
  current: Named<Lazy> | null,
  search: Search
): [readonly Retained[], (name: string) => void] {
  const [entries, setEntries] = useState<readonly Retained[]>(() =>
    retainLayers<Lazy, Search>([], current, search)
  );
  const next = retainLayers(entries, current, search);
  if (next !== entries) {
    setEntries(next);
  }
  const drop = useCallback((key: string) => {
    setEntries((list) => dropExited(list, key));
  }, []);
  const leaving = next
    .filter((entry) => !entry.present)
    .map(layerKey)
    .join(" ");
  useEffect(() => {
    if (leaving === "") {
      return;
    }
    const timer = window.setTimeout(() => {
      for (const key of leaving.split(" ")) {
        drop(key);
      }
    }, EXIT_SAFETY_MS);
    return () => window.clearTimeout(timer);
  }, [leaving, drop]);
  return [next, drop];
}

/**
 * One retained layer, told whether it is present, whether a layer covers
 * it, and how to report its exit.
 */
function RetainedView({
  entry,
  onDone,
  slot,
}: {
  entry: Retained;
  onDone: (key: string) => void;
  slot: LayerSlot;
}) {
  const { covered, present, last, view: View } = entry;
  const key = layerKey(entry);
  const done = useCallback(() => onDone(key), [onDone, key]);
  // A Base UI popup that MOUNTS open skips its starting style — by design,
  // so a page that loads with a dialog open does not animate it in — and
  // every URL layer mounts with the URL already naming it. So a layer
  // arrives closed and opens one commit later, before the first paint, and
  // its enter transition actually runs (`D193`, `T-210`). `Arrival` sits
  // INSIDE the Suspense boundary: a layer whose chunk is still loading must
  // not count as arrived, or it would mount open when the chunk lands.
  const [arrived, setArrived] = useState(false);
  const arrive = useCallback(() => setArrived(true), []);
  const shown = present && arrived;
  const value = useMemo(
    () => ({
      covered,
      done,
      frozen: present && !covered ? undefined : last,
      owned: false,
      present: shown,
      slot,
    }),
    [covered, done, present, shown, last, slot]
  );
  return (
    <LayerPresenceProvider value={value}>
      <Suspense fallback={null}>
        <Arrival onArrive={arrive} />
        <View />
      </Suspense>
    </LayerPresenceProvider>
  );
}

/** Reports, before the first paint, that the layer beside it has mounted. */
function Arrival({ onArrive }: { onArrive: () => void }) {
  useLayoutEffect(() => {
    onArrive();
  }, [onArrive]);
  return null;
}

function renderSlot(
  slot: LayerSlot,
  entries: readonly Retained[],
  onDone: (key: string) => void
): ReactNode[] {
  return entries.map((entry) => (
    <RetainedView
      entry={entry}
      key={`${slot}:${layerKey(entry)}`}
      onDone={onDone}
      slot={slot}
    />
  ));
}

export function Shell() {
  useAndroidBackButton();
  const search = useAppSearch();
  const order = useLocation({ select: (location) => location.state.mpgOrder });
  const overlayAt = useLocation({
    select: (location) => location.state.mpgOpenedAt?.overlay ?? 0,
  });
  const sheetAt = useLocation({
    select: (location) => location.state.mpgOpenedAt?.sheet ?? 0,
  });

  const overlay = overlayOf(search);
  const OverlayView = overlay ? overlayComponent(overlay) : null;
  const SheetView = search.sheet ? SHEETS[search.sheet] : null;
  const overlayOnTop = topLayer(search, order) === "overlay";

  const [overlays, dropOverlay] = useRetained(
    overlay && OverlayView
      ? { at: overlayAt, name: overlay, view: OverlayView }
      : null,
    search
  );
  const [sheets, dropSheet] = useRetained(
    search.sheet && SheetView
      ? { at: sheetAt, name: search.sheet, view: SheetView }
      : null,
    search
  );
  const overlayNodes = renderSlot("overlay", overlays, dropOverlay);
  const sheetNodes = renderSlot("sheet", sheets, dropSheet);

  return (
    <ToastProvider>
      {/* Outside the gate: the service worker raises it whoever is signed in. */}
      <UpdatePromptHost />
      <SessionGate>
        {/* Mounted once, inside the gate, so they run signed in only. */}
        <PushRouting />
        <ClockBridge />
        <WeightUnitBridge />
        <TranscriptPrefetchBridge />
        <WarmLayers />
        <PendingSharedPlanDoor />
        <PendingInviteDoor />
        <AppLayout
          screen={
            <ScreenStack current={screenOf(search)} render={renderScreen} />
          }
        >
          {/* Keyed, so swapping which is on top reorders without a remount. */}
          {overlayOnTop
            ? [...sheetNodes, ...overlayNodes]
            : [...overlayNodes, ...sheetNodes]}
        </AppLayout>
      </SessionGate>
    </ToastProvider>
  );
}
